// Helpers shared by the recorder dialogs and the test editor.

// Every step carries a stable id: healing events and editor state (selection,
// expanded rows, "new" highlights) refer to steps by it, not by index.
export const newStepId = () => crypto.randomUUID();

export const withStepIds = (steps) =>
	steps.map((step) => (step.id ? step : { ...step, id: newStepId() }));

export const getStepIcon = (type) => {
	const icons = {
		navigate: "🌐",
		click: "👆",
		type: "⌨️",
		input: "⌨️",
		select: "📋",
		check: "✓",
		uncheck: "□",
		wait: "⏱️",
	};
	return icons[type] || "▶️";
};

export const getStepDescription = (step) => {
	switch (step.type) {
		case "navigate":
		case "navigation":
			return step.fromClipboard ? "Open the copied link" : `Navigate to ${step.url}`;
		case "newTab":
			return "Open a new tab";
		case "switchTab":
			return `Switch to tab ${step.tabIndex + 1}`;
		case "click":
			return `Click ${step.text || step.selector || "element"}`;
		case "type":
		case "input":
			return `Type "${step.value}" into ${step.selector || "field"}`;
		case "select":
		case "change":
			return `Select "${step.value}" from ${step.selector || "dropdown"}`;
		case "check":
		case "uncheck":
			return `${step.type} ${step.selector || "checkbox"}`;
		case "wait":
			return `Wait ${step.duration}ms`;
		case "visualCheck":
			return `Compare ${step.selector || (step.visual?.fullPage ? "the whole page" : "the visible page")} with its baseline`;
		case "accessibilityCheck":
			return `Check ${step.selector || "the page"} for WCAG ${step.accessibility?.level || "AA"} problems`;
		default:
			return step.selector || step.type;
	}
};

// Steps whose selector identifies an element the executor looks up (and can
// self-heal). Page-level asserts and scrolls use their selector directly.
const ELEMENT_STEP_TYPES = [
	"click",
	"doubleClick",
	"rightClick",
	"input",
	"change",
	"clearInput",
	"hover",
	"dragDrop",
	"waitForElement",
	"type",
	"select",
];

export const isElementStep = (step) =>
	ELEMENT_STEP_TYPES.includes(step.type) ||
	(step.type === "assert" &&
		!["url", "title", "count"].includes(step.assertType));

export const hasFallbackLocators = (step) =>
	(step.locators?.length || 0) + (step.options?.selectors?.length || 0) > 0;

// Drops what the recorder captured about the step's element. After the selector
// is changed by hand these describe the *old* element, and the executor's
// fallback chain would find it again (and a heal would revert the edit).
// For asserts, `text` is the user's description, not the element's text.
export const withoutRecordedTarget = (step) => {
	const {
		locators: _locators,
		elementMeta: _elementMeta,
		tagName: _tagName,
		attributes: _attributes,
		...rest
	} = step;
	if (rest.type !== "assert") delete rest.text;
	return { ...rest, options: { ...rest.options, selectors: [] } };
};

// ── Replay-then-record (ReplayRecordDialog) ──

// {{variable}} substitution for replaying a data-bound test. Same pattern and
// fields as the executor's substituteStep(); unknown variables are left as is.
const substitute = (text, values) =>
	typeof text === "string"
		? text.replace(/\{\{\s*(\w+)\s*}}/g, (match, name) =>
				Object.prototype.hasOwnProperty.call(values, name)
					? values[name]
					: match,
			)
		: text;

export const substituteStep = (step, values) => ({
	...step,
	selector: substitute(step.selector, values),
	value: substitute(step.value, values),
	url: substitute(step.url, values),
	text: substitute(step.text, values),
});

export const substituteText = substitute;

// A data set row as {variable: value}, like DataDrivenExecutionService: the
// test's column mapping, or each column as its own variable.
export const dataValuesForRow = (row, dataSet, columnMapping) => {
	const mapping =
		columnMapping && Object.keys(columnMapping).length > 0
			? columnMapping
			: Object.fromEntries((dataSet.columns || []).map((c) => [c, c]));
	return Object.fromEntries(
		Object.entries(mapping).map(([column, variable]) => [
			variable,
			row?.[column] ?? "",
		]),
	);
};

// Steps as the recorder sends them → steps to store on the test.
export const toTestSteps = (recorded) =>
	withStepIds(recorded).map((step) => ({
		...step,
		timestamp: step.timestamp
			? new Date(step.timestamp).getTime()
			: Date.now(),
	}));

// The element picked in the browser becomes the step's target. Healed
// selectors belonged to the old element; an assert's text is its description.
export const applyPickedTarget = (step, picked) => ({
	...step,
	selector: picked.selector,
	locators: picked.locators,
	elementMeta: picked.elementMeta,
	tagName: picked.tagName,
	attributes: picked.attributes,
	text: step.type === "assert" ? step.text : picked.text,
	options: { ...step.options, selectors: [] },
});
